import { assertNever } from "@/shared/lib/assertNever";
import { addDays, isBefore } from "date-fns";
import type {
  DeliveryProposalOfferCargoStatus,
  OfferCargoProcess,
  OfferCargoProcessStatus,
} from ".";

export const OfferCargoProcessReceptionStatusLabels = [
  { status: "accepting", label: "受付中" },
  { status: "suspended", label: "受付停止" },
  { status: "accepting_with_rejection", label: "受付中" },
  { status: "waiting_response", label: "合意待ち" },
  { status: "closed", label: "受付終了" },
  { status: "contract_concluded", label: "成約" },
] as const;
export type OFFER_CARGO_PROCESS_RECEPTION_STATUS =
  (typeof OfferCargoProcessReceptionStatusLabels)[number]["status"];
export const isOfferCargoProcessReceptionStatus = (
  arg: string | undefined | null,
): arg is OFFER_CARGO_PROCESS_RECEPTION_STATUS =>
  !!arg &&
  OfferCargoProcessReceptionStatusLabels.some(
    (status) => status.status === arg,
  );
export const getOfferCargoProcessReceptionStatusLabel = (
  status: OFFER_CARGO_PROCESS_RECEPTION_STATUS,
): string => {
  const statusLabel = OfferCargoProcessReceptionStatusLabels.find(
    (statusLabel) => statusLabel.status === status,
  );
  if (!statusLabel) {
    throw new Error(`unknown status: ${status}`);
  }
  return statusLabel.label;
};

export const DeliveryProposalContractStatusLabels = [
  { status: "not_responded", label: "未回答" },
  { status: "rejected_by_them", label: "相手見送り" },
  { status: "rejected_by_us", label: "弊社見送り" },
  { status: "waiting_response", label: "合意待ち" },
  { status: "closed", label: "終了" },
  { status: "contract_concluded", label: "成約" },
] as const;
export type DELIVERY_PROPOSAL_CONTRACT_STATUS =
  (typeof DeliveryProposalContractStatusLabels)[number]["status"];
export const isDeliveryProposalContractStatus = (
  arg: string | undefined | null,
): arg is DELIVERY_PROPOSAL_CONTRACT_STATUS =>
  !!arg &&
  DeliveryProposalContractStatusLabels.some((status) => status.status === arg);
export const getDeliveryProposalContractStatusLabel = (
  status: DELIVERY_PROPOSAL_CONTRACT_STATUS,
): string => {
  const statusLabel = DeliveryProposalContractStatusLabels.find(
    (statusLabel) => statusLabel.status === status,
  );
  if (!statusLabel) {
    throw new Error(`unknown status: ${status}`);
  }
  return statusLabel.label;
};

export const OfferCargoProcessProposalStatusLabels = [
  { status: "new", label: "新着" },
  { status: "already_proposed", label: "提案済み" },
] as const;
export type OFFER_CARGO_PROCESS_PROPOSAL_STATUS =
  (typeof OfferCargoProcessProposalStatusLabels)[number]["status"];
export const isOfferCargoProcessProposalStatus = (
  arg: string | undefined | null,
): arg is OFFER_CARGO_PROCESS_PROPOSAL_STATUS =>
  !!arg &&
  OfferCargoProcessProposalStatusLabels.some((status) => status.status === arg);
export const getOfferCargoProcessProposalStatusLabel = (
  status: OFFER_CARGO_PROCESS_PROPOSAL_STATUS,
): string => {
  const statusLabel = OfferCargoProcessProposalStatusLabels.find(
    (statusLabel) => statusLabel.status === status,
  );
  if (!statusLabel) {
    throw new Error(`unknown status: ${status}`);
  }
  return statusLabel.label;
};

export const calculateOfferCargoProcessReceptionStatus = ({
  viewer,
  status,
}: {
  viewer: "shipper" | "carrier";
  status: OfferCargoProcessStatus;
}): OFFER_CARGO_PROCESS_RECEPTION_STATUS => {
  switch (viewer) {
    case "shipper": {
      switch (status) {
        case "no_valid_delivery_proposals":
          return "accepting";
        case "reception_suspended":
          return "suspended";
        case "has_ongoing_proposal_with_no_contract":
          return "accepting";
        case "has_contracts_all_rejected":
          return "accepting_with_rejection";
        case "has_contract_pending":
          return "waiting_response";
        case "proposal_reception_deadline_expired":
          return "closed";
        case "has_contract_concluded":
          return "contract_concluded";
        default:
          assertNever(status);
          throw new Error(`unkown status: ${status}`);
      }
    }
    case "carrier": {
      switch (status) {
        case "no_valid_delivery_proposals":
          return "accepting";
        case "reception_suspended":
          return "closed"; // 車主からは停止されたのか成約したのかはわからないようにする
        case "has_ongoing_proposal_with_no_contract":
          return "accepting";
        case "has_contracts_all_rejected":
          return "accepting";
        case "has_contract_pending":
          return "accepting";
        case "proposal_reception_deadline_expired":
          return "closed";
        case "has_contract_concluded":
          return "closed"; // 車主からは停止されたのか成約したのかはわからないようにする
        default:
          assertNever(status);
          throw new Error(`unkown status: ${status}`);
      }
    }
    default:
      assertNever(viewer);
      throw new Error(`unkown viewer: ${viewer}`);
  }
};

export const calculateDeliveryProposalContractStatus = ({
  viewer,
  status,
}: {
  viewer: "shipper" | "carrier";
  status: DeliveryProposalOfferCargoStatus;
}): DELIVERY_PROPOSAL_CONTRACT_STATUS => {
  switch (status) {
    case "no_valid_contracts":
      return "not_responded";
    case "delivery_proposal_rejected":
      return viewer === "shipper" ? "rejected_by_us" : "rejected_by_them";
    case "has_contracts_all_rejected":
      return viewer === "shipper" ? "rejected_by_them" : "rejected_by_us";
    case "has_contract_pending":
      return "waiting_response";
    case "delivery_proposal_response_deadline_expired":
      return "closed";
    case "has_contract_concluded":
      return "contract_concluded";
    default:
      assertNever(status);
      throw new Error(`unkown status: ${status}`);
  }
};

export const calculateOfferCargoProcessProposalStatuses = ({
  carrierCompanyId,
  offerCargoProcess,
}: {
  carrierCompanyId: number;
  offerCargoProcess: OfferCargoProcess;
}): OFFER_CARGO_PROCESS_PROPOSAL_STATUS[] => {
  const result: OFFER_CARGO_PROCESS_PROPOSAL_STATUS[] = [];
  if (
    isBefore(
      offerCargoProcess.draftOfferCargo.createdAt,
      addDays(new Date(), 1),
    )
  ) {
    result.push("new");
  }
  const myDeliveryProposalExists =
    offerCargoProcess.deliveryProposalOfferCargos.find(
      (deliveryProposalOfferCargo) =>
        deliveryProposalOfferCargo.company?.id === carrierCompanyId,
    );
  if (myDeliveryProposalExists) {
    result.push("already_proposed");
  }
  return result;
};
